// «Прикреплённая карточка» — ВИДИМАЯ часть атрибуции sourceCardTracker.
//
// Юзер кликает витринную карточку (популярка на главной/bots или StylePreset
// из галереи) → полный снапшот карточки (обложка/название/цена/целевая
// модель) прикрепляется сюда и рендерится пином: сбоку виджета /bots, в зоне
// инструкции на /generate, строкой в теле виджета на мобиле. Юзер всегда
// видит, с чем работает.
//
// ЕДИНЫЙ КАНАЛ: attach сам ставит атрибуцию в sourceCardTracker (kind решает
// канал: card → setPendingSourceCard, style → setPendingSourceStyle), detach
// сам её чистит. Пин виден ⇔ атрибуция активна — расхождений по построению
// нет. Механику consumeSourceCardForModel (фиксация модели на первой
// генерации, ленивое снятие) НЕ трогаем — она проверена и живёт как жила.
//
// Persist: sessionStorage (переживает F5 в рамках вкладки, НЕ воскресает в
// новой сессии — как welcome-интент). Подписка компонентов — same-tab
// CustomEvent (паттерн useSelectedWelcomeIntent).
import type { CardPhotoSlot } from '../photo-slots'
import { useEffect, useState } from 'react'
import {
	setPendingSourceCard,
	setPendingSourceStyle,
	clearActiveSourceCard,
	clearActiveSourceStyle,
} from './sourceCardTracker'
import { resolveModelIdAliases } from '../data/brands'
import type { CardKind, GenerationCardSettings, TrendMemberPreview } from '../types/generationCard'

export interface AttachedStyleCard {
	/** null — у карточки нет id (видео-витрина главной): пин без атрибуции */
	id: string | null
	/** card/style — атрибуция разными каналами трекера; video — без атрибуции */
	kind: 'card' | 'style' | 'video'
	title: string
	description?: string
	previewUrl?: string
	/** Видео-обложка (витрина video_showcase) — пин рендерит первый кадр */
	videoUrl?: string
	/** Демо-трек аудио-карточки: пин даёт послушать (play/pause на обложке),
	 *  а виджет Mureka может взять его стилевым референсом генерации */
	audioUrl?: string
	/** Ценник карточки-витрины («45»); у стилей его нет — пин не рендерит цену */
	credits?: string
	targetAdapter?: string
	/** Целевая модель — для авто-открепления при ручной смене модели */
	targetModelId?: string
	/** Момент прикрепления: grace-окно для гонки «attach раньше, чем модель применилась» */
	attachedAt: number
	/** Гость: готовый промпт скрыт бэком (prompt_locked) → виджет показывает CTA
	 *  «зарегистрируйтесь» вместо текста промпта. Для зареганых — undefined. */
	promptLocked?: boolean
	/** Фокус-режим инструмента (карточка «Популярных» на images/video): виджет
	 *  раскрывается в tool-layout вместо классического бара. */
	toolMode?: boolean
	/** Демо для tool-режима: исходник слайдера (до) + вариации стиля
	 *  (after_url для «после», prompt_patch для промпта под капотом;
	 *  is_open — «Свой стиль»: юзер вписывает значение вместо {value}). */
	demoBeforeUrl?: string
	/** Пара «до/после» без вариаций (наглядный слайдер, стилей нет). */
	demoAfterUrl?: string
	demoVariants?: Array<{ key: string; label: string; after_url: string; prompt_patch: string; is_open?: boolean; placeholder?: string; value_type?: 'text' | 'number'; min_value?: number | null; max_value?: number | null; user_input?: { label?: string; placeholder?: string; type?: 'text' | 'number'; min?: number | null; max?: number | null } | null }>
	card_kind?: CardKind
	/** Мульти-тренд (22.10): пин — УЧАСТНИК группы (его цена и референсы), группа — здесь: виджет рисует ряд
	 *  участников, переключение ведёт на лендинг группы (path) с выбранным участником; переживает F5. */
	trendGroup?: { id: string; title: string; path?: string; members: TrendMemberPreview[] }
	/** Транзиент (23.10): участник применён вместо прежнего из той же группы с той же формой загрузок — фото юзера
	 *  (и фото этапов тренда «фото → видео») остались. */
	keepUploads?: boolean
	user_input?: { label?: string; placeholder?: string; type?: 'text' | 'number'; min?: number | null; max?: number | null } | null
	/** Именованные фото-слоты юзера (владелец 2026-08-14): подписи задаёт
	 *  админ, фото уходят referenceImages-массивом В ПОРЯДКЕ слотов — промпт
	 *  карточки ссылается «первое фото»/«второе фото». */
	photo_slots?: CardPhotoSlot[]
	/** Виджет карточки (владелец 2026-08-16): скрыть «Персонажа» (инструментам
	 *  он часто не нужен). */
	hide_character?: boolean
	/** Лимит фото юзера в обычном мульти-входе (1..N, кламп по модели). */
	user_photo_limit?: number
	/** Подпись под слотом «Ваше фото» (как hint у именованных слотов). */
	user_photo_hint?: string
	/** Видео-поля карточки (владелец 2026-08-20). */
	hide_end_image?: boolean
	end_image_hint?: string
	character_hint?: string
	/** Базовый промпт карточки — чтобы после F5 восстановить стиль-селект из пина
	 *  (VariantContext сбрасывается на reload). У гостя пусто (promptLocked). */
	demoBasePrompt?: string
	/** Активная вариация на момент прикрепления — восстановление выбора после F5. */
	activeVariantKey?: string
	/** Введённое юзером значение активной open-вариации («Свой стиль») —
	 *  переживает F5 вместе с пином. */
	customValue?: string
	/** Мультистиль: значение карточного «поля юзера» (возраст и т.п.) —
	 *  ЛЕГАСИ-строка общего поля (пины до 2026-08-16), читается как
	 *  userValues['__card__']. */
	userValue?: string
	/** Значения полей по источнику: '__card__' — общее поле карточки, key
	 *  стиля — его собственное поле (владелец 2026-08-16). Персист F5. */
	userValues?: Record<string, string>
	/** «Своя цена карточки»: floor-оверрайд в кредитах. currentCost и кнопка
	 *  «Создать · N» показывают max(расчётная, оверрайд) — то, что спишет бэк. */
	priceOverride?: number
	/** Конец акции (ISO), когда priceOverride — ПРОМО-цена: пока срок жив,
	 *  currentCost = priceOverride КАК ЕСТЬ (скидки-уровнями промо честно ниже
	 *  базы, бэк спишет её же); истёк → снова floor-семантика max(). */
	discountUntil?: string
	/** Обычная цена источника ДО акции — гейт промо в currentCost: юзер мог
	 *  выбрать настройки ДОРОЖЕ пресета карточки, и тогда бэк спишет базу
	 *  (промо не покроет себестоимость). См. GenerateContext.currentCost. */
	regularPrice?: number
	/** Видео-референс «под капотом» (video-модели, задаёт админ): GTM авто-
	 *  подставляет его в слот «Ваше видео» — юзер добавляет только фото. */
	referenceImageUrl?: string
	userImageLocked?: boolean
	/** Референс есть, а ссылки у клиента нет (19.09): канал занимаем флагом, файл
	 *  подставляет сервер, на просмотр берём короткую подпись (api/cardReference). */
	hasRefVideo?: boolean
	hasRefAudio?: boolean
	/** Длина видео тренда по замеру сервера — витрина отдаёт её при включённой плате за секунды входного видео
	 *  (reference_video_seconds). По ней гостю цена виджета та же, что на плитке, без запроса: замер — только со входом. */
	refVideoSeconds?: number
	referenceVideoUrl?: string
	/** true = слот «Ваше видео» скрыт у юзера (генерация всегда с референсом). */
	userVideoLocked?: boolean
	/** Аудио-референс «под капотом» (модели с аудио-каналом, задаёт админ):
	 *  GTM авто-подставляет его в референс-аудио генерации (audio_urls). */
	referenceAudioUrl?: string
	/** true = аудио-ввод скрыт у юзера (генерация всегда с референсом). */
	userAudioLocked?: boolean
	/** Пайплайн-карточка (тренды «фото → видео»): slug шаблона из админки.
	 *  GTM вместо одиночной генерации ведёт многоэтапный pipeline-режим
	 *  (этапы, версии фото, видео из выбранной). */
	pipelineSlug?: string
	/** Пресет настроек карточки (aspect/quality/…, задаёт админ). Живёт в пине,
	 *  чтобы пережить F5: клик применяет настройки через applyCard-канал, а после
	 *  reload их восстанавливает GenerateProvider (см. эффект пина там) — иначе
	 *  инструмент открывался бы с последними настройками юзера из localStorage. */
	settings?: GenerationCardSettings
}

const STORAGE_KEY = 'umnik.attached_style_card'
const CHANGED_EVENT = 'umnik:attached-style-card-changed'
const DETACHED_EVENT = 'umnik:attached-style-card-detached'

// Grace-окно после attach: клик по карточке ставит пин ДО того, как модель
// успевает примениться (onSelectModel / deep-link ?model= ждёт каталог) —
// mismatch-детектор в это окно молчит, иначе снимал бы свежий пин об старую
// модель.
const MODEL_APPLY_GRACE_MS = 2500

function load(): AttachedStyleCard | null {
	try {
		const raw = sessionStorage.getItem(STORAGE_KEY)
		if (!raw) return null
		const card = JSON.parse(raw) as AttachedStyleCard
		// Гидрация после F5 — ТА ЖЕ ситуация, что и свежий attach: модель ещё не
		// применена (её восстанавливают позже — deep-link ?tool=, каталог моделей,
		// pending-генерация — все в эффектах). Со старым attachedAt из
		// sessionStorage grace-окно всегда «истёкшее» → транзиентный mismatch с
		// дефолтной моделью снимал пин и гасил tool-режим прямо на перезагрузке.
		// Поэтому окно отсчитываем от момента гидрации. attachedAt больше нигде
		// не используется (только в mismatch-детекторе) — семантика не ломается.
		return { ...card, attachedAt: Date.now() }
	} catch {
		return null
	}
}

let current: AttachedStyleCard | null = typeof window !== 'undefined' ? load() : null
// Гидрация после F5 (22.10): пин возвращался, а атрибуция (sourceCardTracker — переменная модуля) нет → запрос
// уходил без source_card_id: «своя цена» и акция карточки не применялись, промпт карточки не скрывался в истории.
// Ставим её так же, как attach — пин виден ⇔ атрибуция активна.
if (current?.id) {
	if (current.kind === 'style') setPendingSourceStyle(current.id)
	else if (current.kind === 'card') setPendingSourceCard(current.id)
}

function emit(): void {
	window.dispatchEvent(new CustomEvent(CHANGED_EVENT))
}

/** Прикрепить карточку (клик по витрине). Ставит и атрибуцию — единый канал. */
export function attachStyleCard(card: Omit<AttachedStyleCard, 'attachedAt'>): void {
	// Та же карточка заново (F5 по ссылке на инструмент, окно «Приложения», лендинг инструмента): введённые значения
	// полей, «Свой вариант» и выбранный стиль не стираем (22.10 — после F5 поле пустело, промпт уходил без значения).
	// Явно заданный стартовый стиль (клик по свотчу витрины) главнее запомненного.
	const same = !!current && !!card.id && current.id === card.id && current.kind === card.kind
	const kept = same && current
		? { customValue: current.customValue, userValues: current.userValues, userValue: current.userValue,
			activeVariantKey: card.activeVariantKey ?? current.activeVariantKey }
		: {}
	current = { ...card, ...kept, attachedAt: Date.now() }
	try {
		sessionStorage.setItem(STORAGE_KEY, JSON.stringify(current))
	} catch {
		/* quota — пин проживёт до F5 */
	}
	// Атрибуция только при наличии id: у видео-витрины его нет — пин
	// информационный, трекер не трогаем (мусорные id в статистику не шлём).
	if (card.id) {
		if (card.kind === 'style') setPendingSourceStyle(card.id)
		else setPendingSourceCard(card.id)
	}
	emit()
}

// ── Конвертеры клик-точек: ЕДИНСТВЕННОЕ место маппинга полей витрин в пин ──
// (правки формата пина не требуют трогать компоненты-витрины).

/** popular_category-карточка (главная HomeTemp / bots PopularModelsTab). */
export function attachFromPopularCard(
	item: {
		id: string
		title: string
		description?: string
		backgroundImageUrl?: string
		creditsLabel?: string
		targetAdapter?: string
		targetModelId?: string
		videoUrl?: string
		audioUrl?: string
		promptLocked?: boolean
		defaultPrompt?: string
		demoBeforeUrl?: string
		demoAfterUrl?: string
		demoVariants?: Array<{ key: string; label: string; after_url: string; prompt_patch: string; is_open?: boolean; placeholder?: string; value_type?: 'text' | 'number'; min_value?: number | null; max_value?: number | null; user_input?: { label?: string; placeholder?: string; type?: 'text' | 'number'; min?: number | null; max?: number | null } | null }>
		cardKind?: CardKind
		trendGroup?: { id: string; title: string; path?: string; members: TrendMemberPreview[] }
		userInput?: { label?: string; placeholder?: string; type?: 'text' | 'number'; min?: number | null; max?: number | null } | null
		photoSlots?: CardPhotoSlot[] | null
		hideCharacter?: boolean
		userPhotoLimit?: number
		userPhotoHint?: string
		hideEndImage?: boolean
		endImageHint?: string
		characterHint?: string
		referenceImageUrl?: string
		userImageLocked?: boolean
		hasRefVideo?: boolean
		hasRefAudio?: boolean
		refVideoSeconds?: number | null
		referenceVideoUrl?: string
		userVideoLocked?: boolean
		referenceAudioUrl?: string
		userAudioLocked?: boolean
		priceOverride?: number
		discountUntil?: string
		regularPrice?: number
		pipelineSlug?: string
		settings?: GenerationCardSettings
	},
	opts?: { toolMode?: boolean; activeVariantKey?: string; keepUploads?: boolean },
): void {
	attachStyleCard({
		id: item.id,
		kind: 'card',
		title: item.title,
		description: item.description || undefined,
		previewUrl: item.backgroundImageUrl || undefined,
		// Живые превью карточки едут в пин как есть: ролик — кадром с
		// контролами, демо-трек — плеером (и референсом для Mureka-генерации).
		videoUrl: item.videoUrl || undefined,
		audioUrl: item.audioUrl || undefined,
		credits: item.creditsLabel || undefined,
		targetAdapter: item.targetAdapter || undefined,
		targetModelId: item.targetModelId || undefined,
		promptLocked: item.promptLocked || undefined,
		// tool-режим: сигнал + демо для слайдера (пин — единый канал в виджет).
		toolMode: opts?.toolMode || undefined,
		// Пайплайн-карточка: GTM ведёт многоэтапный режим (фото → видео).
		pipelineSlug: item.pipelineSlug || undefined,
		demoBeforeUrl: item.demoBeforeUrl || undefined,
		demoAfterUrl: item.demoAfterUrl || undefined,
		demoVariants: item.demoVariants?.length ? item.demoVariants : undefined,
		// Мультистиль: тип карточки + карточное «поле юзера» ({value} в патчах).
		card_kind: item.cardKind || undefined,
		// Мульти-тренд: группа участника — ряд участников в виджете и возврат на её лендинг.
		trendGroup: item.trendGroup || undefined,
		user_input: item.userInput || undefined,
		photo_slots: item.photoSlots?.length ? item.photoSlots : undefined,
		hide_character: item.hideCharacter || undefined,
		user_photo_limit: item.userPhotoLimit || undefined,
		user_photo_hint: item.userPhotoHint || undefined,
		hide_end_image: item.hideEndImage || undefined,
		end_image_hint: item.endImageHint || undefined,
		character_hint: item.characterHint || undefined,
		// База + активная вариация переживают F5 → стиль-селект восстановится из
		// пина (VariantContext на reload пуст). У гостя база пуста (promptLocked).
		demoBasePrompt: item.demoVariants?.length
			? (item.promptLocked ? '' : item.defaultPrompt || '')
			: undefined,
		activeVariantKey: opts?.activeVariantKey || undefined,
		keepUploads: opts?.keepUploads || undefined,
		// Медиа-референсы «под капотом» + локи полей юзера (админ-настройка).
		referenceImageUrl: item.referenceImageUrl || undefined,
		userImageLocked: item.userImageLocked || undefined,
		referenceVideoUrl: item.referenceVideoUrl || undefined,
		userVideoLocked: item.userVideoLocked || undefined,
		referenceAudioUrl: item.referenceAudioUrl || undefined,
		hasRefVideo: item.hasRefVideo || undefined,
		hasRefAudio: item.hasRefAudio || undefined,
		refVideoSeconds: item.refVideoSeconds || undefined,
		userAudioLocked: item.userAudioLocked || undefined,
		priceOverride: item.priceOverride || undefined,
		discountUntil: item.discountUntil || undefined,
		regularPrice: item.regularPrice || undefined,
		// Пресет настроек карточки — переживает F5 вместе с пином.
		settings: item.settings,
	})
}

/** StylePreset из галерей стилей (/generate applyCard и bots handleStyleApply).
 *  У видео-стилей previewUrl — это ролик: раскладываем в videoUrl, чтобы пин
 *  рендерил кадр видео, а не битый <img>.
 *  opts.toolMode — стиль-«инструмент» (зеркало «Популярных», есть демо
 *  «до/после»): /generate открывает фокус-режим GenerateToolMode; демо-поля
 *  пробрасываются в пин для слайдера/стиль-селекта. */
export function attachFromStyleCard(
	card: {
		id: string
		title?: string
		previewUrl?: string
		modelId?: string
		promptLocked?: boolean
		prompt?: string
		demoBeforeUrl?: string
		demo_after_url?: string
		demoVariants?: Array<{ key: string; label: string; after_url: string; prompt_patch: string; is_open?: boolean; placeholder?: string; value_type?: 'text' | 'number'; min_value?: number | null; max_value?: number | null; user_input?: { label?: string; placeholder?: string; type?: 'text' | 'number'; min?: number | null; max?: number | null } | null }>
		card_kind?: CardKind
		user_input?: { label?: string; placeholder?: string; type?: 'text' | 'number'; min?: number | null; max?: number | null } | null
		photo_slots?: CardPhotoSlot[]
		hide_character?: boolean
		user_photo_limit?: number
		user_photo_hint?: string
		/** Форс фокус-режима (тренды открываются инструментом всегда). */
		toolMode?: boolean
		priceOverride?: number
		discountUntil?: string
		regularPrice?: number
		referenceImageUrl?: string
		userImageLocked?: boolean
		hasRefVideo?: boolean
		hasRefAudio?: boolean
		referenceVideoUrl?: string
		userVideoLocked?: boolean
		referenceAudioUrl?: string
		userAudioLocked?: boolean
		settings?: GenerationCardSettings
	},
	opts?: { toolMode?: boolean; activeVariantKey?: string },
): void {
	const preview = card.previewUrl || undefined
	const isVideoPreview = !!preview && /\.(mp4|mov|webm|m4v)(\?|$)/i.test(preview)
	attachStyleCard({
		id: card.id,
		kind: 'style',
		title: card.title || 'Стиль',
		previewUrl: isVideoPreview ? undefined : preview,
		videoUrl: isVideoPreview ? preview : undefined,
		targetModelId: card.modelId || undefined,
		promptLocked: card.promptLocked || undefined,
		// Стиль с полями «инструмента» (референс/лок из админки) открывается
		// фокус-режимом ОТОВСЮДУ (и из Галереи стилей): подстановка референса
		// и скрытие полей живут в GenerateToolMode — обычный бар их не умеет.
		toolMode:
			opts?.toolMode ||
			card.toolMode ||
			!!(card.referenceImageUrl || card.referenceVideoUrl || card.referenceAudioUrl ||
				card.hasRefVideo || card.hasRefAudio ||
				card.userImageLocked || card.userVideoLocked || card.userAudioLocked) ||
			undefined,
		demoBeforeUrl: card.demoBeforeUrl || undefined,
		demoAfterUrl: card.demo_after_url || undefined,
		demoVariants: card.demoVariants?.length ? card.demoVariants : undefined,
		card_kind: card.card_kind || undefined,
		user_input: card.user_input || undefined,
		photo_slots: card.photo_slots?.length ? card.photo_slots : undefined,
		hide_character: card.hide_character || undefined,
		user_photo_limit: card.user_photo_limit || undefined,
		user_photo_hint: card.user_photo_hint || undefined,
		demoBasePrompt: card.promptLocked ? undefined : card.prompt || undefined,
		activeVariantKey: opts?.activeVariantKey || undefined,
		// Поля «инструмента» стиля — та же механика, что у карточек Популярных:
		// floor-цена в `currentCost`, референсы «под капотом» + локи в GTM.
		priceOverride: card.priceOverride || undefined,
		discountUntil: card.discountUntil || undefined,
		regularPrice: card.regularPrice || undefined,
		referenceImageUrl: card.referenceImageUrl || undefined,
		userImageLocked: card.userImageLocked || undefined,
		referenceVideoUrl: card.referenceVideoUrl || undefined,
		userVideoLocked: card.userVideoLocked || undefined,
		referenceAudioUrl: card.referenceAudioUrl || undefined,
		hasRefVideo: card.hasRefVideo || undefined,
		hasRefAudio: card.hasRefAudio || undefined,
		userAudioLocked: card.userAudioLocked || undefined,
		// Пресет настроек стиля (aspect/quality/…) — переживает F5 вместе с пином.
		settings: card.settings,
	})
}

/** Видео-витрина главной (video_showcase): id нет — пин без атрибуции.
 *  opts.toolMode — как у attachFromStyleCard: /generate открывает фокус-режим
 *  GenerateToolMode, ролик карточки становится демо-превью инструмента. */
export function attachFromVideoItem(
	video: {
		/** id айтема витрины (новые записи; у legacy нет — пин без атрибуции). */
		id?: string
		title?: string
		description?: string
		url: string
		target_adapter?: string
		target_model?: string
		reference_image_url?: string
		user_image_locked?: boolean
		has_reference_video?: boolean
		has_reference_audio?: boolean
		reference_video_seconds?: number | null
		reference_video_url?: string
		user_video_locked?: boolean
		reference_audio_url?: string
		user_audio_locked?: boolean
		pipeline_slug?: string
		settings?: GenerationCardSettings
	},
	opts?: { toolMode?: boolean; priceOverride?: number; discountUntil?: string; regularPrice?: number },
): void {
	// В витрине живут И видео, И фото-слайды: фото кладём в previewUrl —
	// иначе GTM рендерил его <video>-тегом (не играет) и демо-зона пустела.
	const isVideoMedia = /\.(mp4|mov|webm|m4v)(\?|$)/i.test(video.url)
	attachStyleCard({
		id: video.id || null,
		kind: 'video',
		title: video.title?.trim() || 'Видео-шаблон',
		description: video.description || undefined,
		previewUrl: isVideoMedia ? undefined : video.url,
		videoUrl: isVideoMedia ? video.url : undefined,
		targetAdapter: video.target_adapter || undefined,
		targetModelId: video.target_model || undefined,
		toolMode: opts?.toolMode || undefined,
		pipelineSlug: video.pipeline_slug || undefined,
		// «Своя цена» айтема (эффективная — со скидкой, посчитана на входе).
		priceOverride: opts?.priceOverride || undefined,
		discountUntil: opts?.discountUntil || undefined,
		regularPrice: opts?.regularPrice || undefined,
		// Медиа-референсы «под капотом» + локи полей юзера (админ-настройка).
		referenceImageUrl: video.reference_image_url || undefined,
		userImageLocked: video.user_image_locked || undefined,
		referenceVideoUrl: video.reference_video_url || undefined,
		userVideoLocked: video.user_video_locked || undefined,
		referenceAudioUrl: video.reference_audio_url || undefined,
		hasRefVideo: video.has_reference_video || undefined,
		hasRefAudio: video.has_reference_audio || undefined,
		refVideoSeconds: video.reference_video_seconds || undefined,
		userAudioLocked: video.user_audio_locked || undefined,
		// Пресет настроек айтема — переживает F5 вместе с пином.
		settings: video.settings,
	})
}

/** «Свой стиль»: точечный патч значения инпута в текущем пине (персист F5).
 *  emit НЕ шлём: значение печатается на каждый ввод, ре-рендер подписчиков
 *  не нужен — GenerateToolMode держит своё локальное состояние инпута. */
export function setAttachedCustomValue(value: string): void {
	if (!current) return
	current = { ...current, customValue: value }
	try {
		sessionStorage.setItem(STORAGE_KEY, JSON.stringify(current))
	} catch {
		/* quota — значение проживёт до F5 */
	}
}

/** Мультистиль: точечный патч значения карточного «поля юзера» в пине —
 *  та же механика, что setAttachedCustomValue (тихо, без emit). */
export function setAttachedUserValue(valueKey: string, value: string): void {
	if (!current) return
	current = { ...current, userValues: { ...(current.userValues ?? {}), [valueKey]: value } }
	try {
		sessionStorage.setItem(STORAGE_KEY, JSON.stringify(current))
	} catch {
		/* quota — значение проживёт до F5 */
	}
}

/** Выбранный стиль мультистиля — в пин (тихо, без emit): после F5 возвращаемся к нему, а не к стартовому, и по
 *  нему же запрос решает, чьё значение уходит полем user_prompt (variantUserText). */
export function setAttachedActiveVariant(key: string): void {
	if (!current || current.activeVariantKey === key) return
	current = { ...current, activeVariantKey: key }
	try {
		sessionStorage.setItem(STORAGE_KEY, JSON.stringify(current))
	} catch {
		/* quota — выбор проживёт до F5 */
	}
}

/** Открепить (крестик / смена модели / другая карточка / уход со страницы). */
export function detachStyleCard(): void {
	if (!current) return
	const prev = current
	current = null
	try {
		sessionStorage.removeItem(STORAGE_KEY)
	} catch {
		/* ignore */
	}
	clearActiveSourceCard()
	clearActiveSourceStyle()
	// Прошлая карточка — подписчикам очистки (см. subscribeStyleCardDetached):
	// решение «что гасить» зависит от того, ЧТО было прикреплено (tool-промпт
	// vs свободный текст юзера), а после обнуления current этого уже не узнать.
	window.dispatchEvent(new CustomEvent(DETACHED_EVENT, { detail: prev }))
	emit()
}

/** Подписка на факт открепления С прошлой карточкой в аргументе — для логики,
 *  которой важно, что именно было прикреплено (CHANGED_EVENT этого не несёт:
 *  к моменту события стор уже пуст). Возвращает отписку. */
export function subscribeStyleCardDetached(
	cb: (card: AttachedStyleCard) => void,
): () => void {
	const handler = (e: Event) => cb((e as CustomEvent<AttachedStyleCard>).detail)
	window.addEventListener(DETACHED_EVENT, handler)
	return () => window.removeEventListener(DETACHED_EVENT, handler)
}

export function getAttachedStyleCard(): AttachedStyleCard | null {
	return current
}

/** Колбэк-подписка на изменение прикреплённой карточки — для НЕ-рендер логики
 *  (напр. очистка связанных демо-чипов при откреплении). Тот же CHANGED_EVENT,
 *  что и useAttachedStyleCard, но без React-состояния. Возвращает отписку. */
export function subscribeAttachedStyleCard(cb: () => void): () => void {
	window.addEventListener(CHANGED_EVENT, cb)
	return () => window.removeEventListener(CHANGED_EVENT, cb)
}

/** Реактивная подписка на прикреплённую карточку. */
export function useAttachedStyleCard(): AttachedStyleCard | null {
	const [card, setCard] = useState<AttachedStyleCard | null>(current)
	useEffect(() => {
		const reload = () => setCard(getAttachedStyleCard())
		window.addEventListener(CHANGED_EVENT, reload)
		// Догоняем emit, случившийся МЕЖДУ рендером (useState захватил current)
		// и этой подпиской: при смене роута cleanup уходящего дерева (detach на
		// анмаунте виджета /bots) стреляет раньше mount-эффектов нового — свежий
		// компонент оставался с карточкой-фантомом. Совпало — React bailout.
		reload()
		return () => window.removeEventListener(CHANGED_EVENT, reload)
	}, [])
	return card
}

/**
 * Активен ли аудио-референс: у карточки есть демо-трек И выбрана Mureka-модель
 * (стилевой референс — только её API; Suno такого не умеет). Никакого
 * отдельного тумблера: карточка прикреплена = референс работает, открепил
 * крестиком = обычная генерация. Единый гейт для пина (лейбл) и сабмита.
 */
export function isAudioReferenceActive(
	card: AttachedStyleCard | null,
	model: { id: string } | null | undefined,
): boolean {
	return !!card?.audioUrl && !!model && model.id.toLowerCase().startsWith('mureka')
}

/** Совпадает ли карточка с моделью (alias-aware, как deep-link /generate). */
export function cardMatchesModel(
	card: AttachedStyleCard,
	model: { id: string; backendModel?: string } | null | undefined,
): boolean {
	if (!model) return true // модель ещё грузится — не дёргаем пин
	if (!card.targetModelId) return true // стиль без целевой модели живёт при любой
	const ids = resolveModelIdAliases(card.targetModelId)
	return ids.includes(model.id) || (!!model.backendModel && ids.includes(model.backendModel))
}

/**
 * Модель карточки, прикреплённой только что (grace-окно после attach или гидрации после F5), если она есть в
 * каталоге; иначе null. На лендинге карточки её модель — выбор человека: контекст виджета /bots не перебивает её
 * моделью раздела при смене адреса (клик по тренду сам ведёт на /tool/<slug>, 28.09).
 */
export function freshCardModel<M extends { id: string; backendModel?: string }>(
	models: M[],
	now: number = Date.now(),
): M | null {
	const card = current
	if (!card?.targetModelId || now - card.attachedAt >= MODEL_APPLY_GRACE_MS) return null
	return models.find((m) => cardMatchesModel(card, m)) ?? null
}

/**
 * Авто-открепление при ручной смене модели: рецепт карточки сломан — держать
 * пин «Видео с вашим лицом» на другой нейронке было бы враньём. Снятие тихое
 * (без тостов). Grace-окно после attach пережидает применение модели.
 */
export function useDetachOnModelMismatch(
	model: { id: string; backendModel?: string } | null | undefined,
	skip = false,
): void {
	const card = useAttachedStyleCard()
	useEffect(() => {
		// Идёт генерация (skip=true): держим пин. На возврате на /bots-медиа
		// модель ещё гидрируется из pending-генерации (в эффекте, позже маунта),
		// а grace-окно у старого пина истекло — иначе транзиентный mismatch снял
		// бы tool-режим до применения модели.
		if (skip) return
		if (!card || cardMatchesModel(card, model)) return
		const elapsed = Date.now() - card.attachedAt
		if (elapsed >= MODEL_APPLY_GRACE_MS) {
			detachStyleCard()
			return
		}
		// Внутри grace-окна: перепроверяем по его истечении (модель либо
		// доехала и совпала, либо это правда ручная смена — снимаем).
		const t = window.setTimeout(() => {
			const still = getAttachedStyleCard()
			if (still && still.id === card.id && !cardMatchesModel(still, model)) {
				detachStyleCard()
			}
		}, MODEL_APPLY_GRACE_MS - elapsed)
		return () => window.clearTimeout(t)
	}, [card, model, skip])
}
